import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AllStatusWrapper } from '../../../../components/execution/terminal/AllStatusWrapper'
import { UpiIntentRedirectModal } from '../../../../components/execution/upi/UpiIntentRedirectModal'
import { PaymentNotCompletedBottomSheet } from '../../../../components/BottomSheetComponents/mf-execution/PaymentNotCompletedBottomSheet'
import { useAutoPayStatusQuery } from '../../../../hooks/mf-execution/useAutoPayStatusQuery'
import useTimer from '../../../../hooks/useTimer'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import {
  AutoPaySetupStatus,
  TerminalStatus,
  TerminalStatusContext,
} from '../../../../types/mf-execution.types'
import { postCTA, upiPay } from '../../../../utils/appBridge'
import { getOrdersTabCTA } from '../../../../utils/mutualFundDetail'
import { isUpiIntentsFlowEnabled } from '../../../../utils/mf-execution/upiIntentsFeature'
import { convertMillisecondsToMinutesAndSeconds } from '../../../../utils/utils'

const AUTO_PAY_UPI_STATUS_MAX_TIMER_MS = 120000

/**
 * UPI Autopay mandate setup waiting screen (v2).
 *
 * Replaces `AutoPayLinkWaitingPage` when setup was initiated with `mandateType: UPI_AUTOPAY`.
 * Polls `useAutoPayStatusQuery` (with `upiPackageName` in the URL), shows terminal states via
 * `AllStatusWrapper`, launches the UPI intent when `link` arrives, then navigates immediately to
 * `/sip/auto-pay/status` on SUCCESS (success UX lives on `AutoPayStatusPage`).
 *
 * Net banking continues to use `AutoPayLinkWaitingPage` (redirect via `link` field).
 */
export function AutoPayUpiLinkWaitingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const [status, setStatus] = useState<TerminalStatus>(TerminalStatus.LOADING)
  const [showUpiRedirectModal, setShowUpiRedirectModal] = useState(false)
  const hasTriggeredUpiIntentRef = useRef(false)
  const hasHandledSuccessRef = useRef(false)

  const sxpId = searchParams.get('sxpId')
  const mandateId = searchParams.get('mandateId')

  useEffect(() => {
    hasTriggeredUpiIntentRef.current = false
    hasHandledSuccessRef.current = false
    setShowUpiRedirectModal(false)
  }, [sxpId, mandateId])

  const upiPackageNameFromUrl = searchParams.get('upiPackageName')

  const { timeLeft, isExpired: isTimerExpired } = useTimer({ maxTimer: AUTO_PAY_UPI_STATUS_MAX_TIMER_MS })
  const { autoPayStatus, isLoading, isError, isExpired: isPollExpired } = useAutoPayStatusQuery()

  const isExpired = isTimerExpired || isPollExpired
  const resolvedUpiPackageName = autoPayStatus?.upiPackageName ?? upiPackageNameFromUrl

  const isUpiIntentAwaiting =
    isUpiIntentsFlowEnabled() &&
    autoPayStatus?.status === AutoPaySetupStatus.INITIATED &&
    !!autoPayStatus?.link &&
    !isError

  useEffect(() => {
    const upiLink = autoPayStatus?.link

    if (!isUpiIntentAwaiting || !upiLink || hasTriggeredUpiIntentRef.current) {
      if (!isUpiIntentAwaiting) {
        setShowUpiRedirectModal(false)
      }
      return
    }

    setShowUpiRedirectModal(true)

    const redirectTimeoutId = window.setTimeout(() => {
      hasTriggeredUpiIntentRef.current = true
      upiPay({
        upiLink,
        packageName: resolvedUpiPackageName,
      })
      setShowUpiRedirectModal(false)
    }, 1000)

    return () => window.clearTimeout(redirectTimeoutId)
  }, [isUpiIntentAwaiting, autoPayStatus?.link, resolvedUpiPackageName])

  useEffect(() => {
    if (isError || autoPayStatus?.status === AutoPaySetupStatus.FAILED) {
      setStatus(TerminalStatus.FAILURE)
      return
    }

    if (autoPayStatus?.status === AutoPaySetupStatus.SUCCESS) {
      if (hasHandledSuccessRef.current) {
        return
      }
      hasHandledSuccessRef.current = true
      // Success UX (partial → full) lives on AutoPayStatusPage; skip duplicate partial state here.
      navigate(`/sip/auto-pay/status?${searchParams.toString()}`)
      return
    }

    if (autoPayStatus?.status === AutoPaySetupStatus.INITIATED) {
      setStatus(TerminalStatus.AWAITING)
      return
    }

    if (isLoading) {
      setStatus(TerminalStatus.LOADING)
    }
  }, [autoPayStatus, isError, isLoading, navigate, searchParams])

  useEffect(() => {
    if (isExpired && (status === TerminalStatus.LOADING || status === TerminalStatus.AWAITING)) {
      setStatus(TerminalStatus.PENDING)
    }
  }, [isExpired, status])

  const handleBackButtonClick = () => {
    openBottomSheet(
      <PaymentNotCompletedBottomSheet
        onDismiss={closeBottomSheet}
        onStayOnScreen={closeBottomSheet}
        onExitAnyway={() => {
          closeBottomSheet()
          postCTA(JSON.stringify(getOrdersTabCTA()))
        }}
        ScreenName='AutoPayUpiLinkWaitingPage'
        isAutopay
      />
    )
  }

  return (
    <div className='w-full h-screen relative overflow-hidden'>
      {showUpiRedirectModal ? (
        <UpiIntentRedirectModal
          upiPackageName={resolvedUpiPackageName}
          actionUrl={autoPayStatus?.link}
        />
      ) : null}
      <AllStatusWrapper
        status={status}
        statusContext={TerminalStatusContext.AUTO_PAY}
        loadingConfig={{
          title: 'Setting up Autopay',
        }}
        awaitingConfig={{
          title: 'Awaiting confirmation',
          subtitle: `Please wait while we confirm your autopay setup`,
          timerText: convertMillisecondsToMinutesAndSeconds(timeLeft),
          caption: 'Do not close this screen',
          onBackButtonClick: handleBackButtonClick,
        }}
        failureConfig={{
          title: 'Autopay setup failed',
          caption: 'We couldn’t complete your Autopay setup at the moment.',
          ctas: [
            {
              ctaType: 'primary',
              label: 'Close',
              onClick: () => {
                postCTA(JSON.stringify(getOrdersTabCTA()))
              },
            },
          ],
        }}
        pendingConfig={{
          title: 'Autopay setup pending',
          caption: 'It’s taking longer than expected. We’ll notify you once Autopay is set.',
          ctas: [
            {
              ctaType: 'primary',
              label: 'Close',
              onClick: () => {
                postCTA(JSON.stringify(getOrdersTabCTA()))
              },
            },
          ],
        }}
      />
    </div>
  )
}
